import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function jwtDecode(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

let refreshPromise = null;

function getStoredSession() {
  if (localStorage.getItem('token')) {
    return {
      storage: localStorage,
      tokenKey: 'token',
      refreshTokenKey: 'refresh_token',
    };
  }
  return {
    storage: sessionStorage,
    tokenKey: 'token_session',
    refreshTokenKey: 'refresh_token_session',
  };
}

function clearStoredSession() {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('refresh_token');
  sessionStorage.removeItem('token_session');
  sessionStorage.removeItem('user_session');
  sessionStorage.removeItem('refresh_token_session');
  localStorage.removeItem('court_portal_admin_token');
  localStorage.removeItem('court_portal_admin_user');
  sessionStorage.removeItem('court_portal_admin_token');
  sessionStorage.removeItem('court_portal_admin_user');
}

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token_session');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && originalRequest) {
      const adminStorage = localStorage.getItem('court_portal_admin_token')
        ? localStorage
        : (sessionStorage.getItem('court_portal_admin_token') ? sessionStorage : null);
      const persistent = adminStorage === localStorage;
      const activeToken = adminStorage?.getItem(persistent ? 'token' : 'token_session');
      const adminToken = adminStorage?.getItem('court_portal_admin_token');
      if (adminStorage && activeToken !== adminToken) {
        adminStorage.setItem(persistent ? 'token' : 'token_session', adminToken);
        adminStorage.setItem(persistent ? 'user' : 'user_session', adminStorage.getItem('court_portal_admin_user'));
        window.location.href = '/select-owner';
        return Promise.reject(error);
      }

      const isRefreshRequest = originalRequest.url?.includes('/auth/refresh');
      const session = getStoredSession();
      const refreshToken = session.storage.getItem(session.refreshTokenKey);
      if (!originalRequest._retry && !isRefreshRequest && refreshToken) {
        originalRequest._retry = true;
        try {
          refreshPromise ??= axios
            .post(`${API_BASE_URL}/auth/refresh`, { refreshToken })
            .then(({ data }) => {
              session.storage.setItem(session.tokenKey, data.token);
              if (data.refreshToken) {
                session.storage.setItem(session.refreshTokenKey, data.refreshToken);
              }
              return data.token;
            })
            .finally(() => {
              refreshPromise = null;
            });
          const token = await refreshPromise;
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        } catch {
          // The refresh token is no longer valid; continue to normal logout.
        }
      }

      clearStoredSession();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export { jwtDecode };
export default api;
